/**
 * Geconsolideerd jaarverslag bij meerdere VTP's/BMW'ers per werkgever (migratie 174).
 * Zie src/routes/teamJaarverslag.ts. Een 409 op `bewaarTekst` geeft in
 * `response.data.data` de actuele tekst en versie van de collega terug.
 */
import api from './api';
import type { Analytics } from './caseRegistratieApi';

const d = <T,>(r: { data: { data: T } }) => r.data.data;
const BASIS = '/professional/team-jaarverslag';

/** Codes uit HOOFDSTUKKEN per discipline (BMW migratie 185, VTP 190), plus terugblik_<n> (BMW H8, VTP H9). */
export type Hoofdstuk = string;

export interface TekstRegel {
  code: Hoofdstuk;
  nummer: string;
  titel: string;
  tekst: string;
  versie: number;
  bijgewerkt_op: string | null;
  bijgewerkt_door: string | null;
  /** Naam van de collega die dit hoofdstuk nu vasthoudt (typt); null = vrij (migratie 189). */
  bezig_door: string | null;
  /** Open suggesties van het team, plus je eigen behandelde. */
  suggesties: Suggestie[];
}

export interface Suggestie {
  id: string;
  naam: string;
  tekst: string;
  status: 'open' | 'verwerkt' | 'afgewezen';
  ingediend_op: string;
  is_mijn: boolean;
}

/** Redacteur schrijft mee; bijdrager leest mee en dient suggesties in. De coördinator schrijft altijd. */
export type Redactierol = 'redacteur' | 'bijdrager';

/** Wat het verslag naast cijfers en teksten bevat; na de release zoals bevroren. */
export interface VasteOnderdelen {
  contactpersonen: Array<{ naam: string; email: string | null; telefoon: string | null; register_geverifieerd: boolean }>;
  /** gemiddelde/aantal null = minder evaluaties dan min_groep. */
  tevredenheid: { aantal: number | null; gemiddelde: number | null; min_groep: number } | null;
  /** VTP H6.1 (migratie 190): alleen tellingen; BMW null. */
  activiteiten: { zichtbaarheidsrondes: number; werkvloergesprekken: number; workshops: number; overleggen: number } | null;
  /** VTP H6.2: per actieve vertrouwenspersoon, zoals het Kwaliteitspaspoort telt; BMW null. */
  beroepsborging: Array<{ naam: string; register_geverifieerd: boolean; intervisie_sessies: number; pe_punten: number }> | null;
  /** null = geen vrijgegeven teamverslag over het vorige jaar. */
  terugblik: { jaar: number; items: Array<{ aanbeveling: string; evaluatie: TekstRegel }> } | null;
}

export interface Team {
  discipline: 'VTP' | 'BMW';
  ik_ben_coordinator: boolean;
  ik_mag_schrijven: boolean;
  coordinator_vacant: boolean;
  leden: Array<{ professional_id: string; naam: string; is_coordinator: boolean; redactierol: Redactierol; mag_schrijven: boolean; is_ik: boolean }>;
}

export interface TeamJaar {
  jaar: number;
  discipline: 'VTP' | 'BMW';
  status: 'concept' | 'released';
  vrijgegeven_op: string | null;
  verificatie_url: string | null;
  ik_ben_coordinator: boolean;
  ik_mag_schrijven: boolean;
  audit_verzoek_op: string | null;
  deelnemers: Array<{ professional_id: string; naam: string; actief: boolean; geverifieerd: boolean; is_ik: boolean }>;
  nog_te_verifieren: number;
  wachtend_op_bevestiging: number;
  teksten: TekstRegel[];
  preview: Analytics | null;
  /** null = vrijgegeven vóór migratie 185 (BMW) / 190 (VTP). */
  onderdelen: VasteOnderdelen | null;
}

export const getTeam = (employerId: string) => api.get<{ data: Team }>(BASIS, { params: { employer_id: employerId } }).then(d);
export const draagOver = (employerId: string, professionalId: string) =>
  api.post<{ data: Team }>(`${BASIS}/coordinator/overdragen`, { employer_id: employerId, professional_id: professionalId }).then(d);
export const claimCoordinator = (employerId: string) => api.post<{ data: Team }>(`${BASIS}/coordinator/claimen`, { employer_id: employerId }).then(d);
export const verlaatTeam = (employerId: string) => api.post(`${BASIS}/verlaten`, { employer_id: employerId });
export const getTeamJaar = (employerId: string, jaar: number) =>
  api.get<{ data: TeamJaar }>(`${BASIS}/jaar`, { params: { employer_id: employerId, jaar } }).then(d);
export const zetVerificatie = (employerId: string, jaar: number, geverifieerd: boolean) =>
  api.post<{ data: TeamJaar }>(`${BASIS}/jaar/verificatie`, { employer_id: employerId, jaar, geverifieerd }).then(d);
export const bewaarTekst = (employerId: string, jaar: number, hoofdstuk: Hoofdstuk, tekst: string, versie: number) =>
  api.put<{ data: { hoofdstuk: Hoofdstuk; tekst: string; versie: number } }>(`${BASIS}/jaar/teksten/${hoofdstuk}`, { employer_id: employerId, jaar, tekst, versie }).then(d);
export const zetRedactierol = (employerId: string, professionalId: string, rol: Redactierol) =>
  api.post<{ data: Team }>(`${BASIS}/rol`, { employer_id: employerId, professional_id: professionalId, rol }).then(d);
/** `bezig` neemt of verlengt het slot op een hoofdstuk; 423 met `data.bezig_door` als een collega het heeft. */
export const zetSectieSlot = (employerId: string, jaar: number, hoofdstuk: Hoofdstuk, bezig: boolean) =>
  api.post(`${BASIS}/jaar/teksten/${hoofdstuk}/slot`, { employer_id: employerId, jaar, bezig });
export const dienSuggestieIn = (employerId: string, jaar: number, hoofdstuk: Hoofdstuk, tekst: string) =>
  api.post<{ data: TeamJaar }>(`${BASIS}/jaar/teksten/${hoofdstuk}/suggesties`, { employer_id: employerId, jaar, tekst }).then(d);
export const behandelSuggestie = (employerId: string, jaar: number, id: string, actie: 'overnemen' | 'afwijzen') =>
  api.post<{ data: TeamJaar }>(`${BASIS}/jaar/suggesties/${id}`, { employer_id: employerId, jaar, actie }).then(d);
export const vraagAudit = (employerId: string, jaar: number) =>
  api.post<{ data: { aantal: number } }>(`${BASIS}/jaar/audit-verzoek`, { employer_id: employerId, jaar }).then(d);
export const releaseTeam = (employerId: string, jaar: number) =>
  api.post<{ data: { id: string; jaar: number; released_at: string; verificatie_url: string; aantal_vertrouwenspersonen: number } }>(`${BASIS}/jaar/release`, {
    employer_id: employerId,
    jaar,
  }).then(d);

export interface JaarverslagVerificatie {
  /** Zegel herberekend over de opgeslagen cijfers en teksten; false = na vrijgave aangepast. */
  geldig: boolean;
  jaar: number;
  discipline: string;
  opdrachtgever: string;
  vrijgegeven_op: string;
  zegel: string;
  aantal_vertrouwenspersonen: number;
  aantal_casussen: number | null;
  aantal_gesprekken: number | null;
}

/** Publiek, zonder login: bestemming van de QR-code in de PDF. */
export const verifieerJaarverslag = (code: string) =>
  api.get<{ data: JaarverslagVerificatie }>(`/jaarverslag-verificatie/${encodeURIComponent(code)}`).then(d);
